import { formatInTimeZone } from 'date-fns-tz';
import { parse, format, addMinutes } from 'date-fns';

export interface TimezoneOption {
  id: string; // e.g. "Europe/Berlin"
  label: string; // e.g. "Europe/Berlin (GMT+2)"
  city: string; // e.g. "Berlin"
  offset: string; // e.g. "GMT+2"
}

export interface TimeSlot {
  id: string;
  start: string; // "HH:mm"
  end: string; // "HH:mm"
}

export interface DaySchedule {
  enabled: boolean;
  slots: TimeSlot[];
}

export type DayKey =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

export interface WeeklySchedule {
  monday: DaySchedule;
  tuesday: DaySchedule;
  wednesday: DaySchedule;
  thursday: DaySchedule;
  friday: DaySchedule;
  saturday: DaySchedule;
  sunday: DaySchedule;
}

export const DAYS_CONFIG: { key: DayKey; label: string }[] = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

export const DEFAULT_WEEKLY_SCHEDULE: WeeklySchedule = {
  monday: {
    enabled: true,
    slots: [{ id: 'slot-mon-1', start: '09:00', end: '17:00' }],
  },
  tuesday: {
    enabled: true,
    slots: [{ id: 'slot-tue-1', start: '09:00', end: '17:00' }],
  },
  wednesday: {
    enabled: true,
    slots: [{ id: 'slot-wed-1', start: '09:00', end: '17:00' }],
  },
  thursday: {
    enabled: true,
    slots: [{ id: 'slot-thu-1', start: '09:00', end: '17:00' }],
  },
  friday: {
    enabled: true,
    slots: [{ id: 'slot-fri-1', start: '09:00', end: '17:00' }],
  },
  saturday: {
    enabled: false,
    slots: [],
  },
  sunday: {
    enabled: false,
    slots: [],
  },
};

export const TIME_SLOTS_30_MIN: string[] = (() => {
  const slots: string[] = [];
  for (let h = 0; h < 24; h++) {
    const hh = h.toString().padStart(2, '0');
    slots.push(`${hh}:00`);
    slots.push(`${hh}:30`);
  }
  return slots;
})();

export const END_TIME_SLOTS_30_MIN: string[] = [...TIME_SLOTS_30_MIN.slice(1), '24:00'];

export function timeToMinutes(timeStr: string): number {
  if (!timeStr) return 0;
  if (timeStr === '24:00') return 1440;
  try {
    const baseDate = parse(timeStr, 'HH:mm', new Date(2000, 0, 1));
    return baseDate.getHours() * 60 + baseDate.getMinutes();
  } catch {
    const [hStr, mStr] = timeStr.split(':');
    return (parseInt(hStr, 10) || 0) * 60 + (parseInt(mStr, 10) || 0);
  }
}

export function minutesToTime(minutes: number): string {
  if (minutes >= 1440) return '24:00';
  const baseDate = addMinutes(new Date(2000, 0, 1, 0, 0, 0), minutes);
  return format(baseDate, 'HH:mm');
}

/**
 * Format timezone using date-fns-tz with fallback for Safari/edge cases
 */
export function formatTimezoneOption(tz: string): TimezoneOption {
  try {
    const now = new Date();

    let offset = formatInTimeZone(now, tz, 'O');

    // ➡️ fallback if offset is not useful (Safari / edge cases)
    if (!offset || offset === 'GMT') {
      const raw = formatInTimeZone(now, tz, 'xxx'); // +05:30
      offset = `GMT${raw}`;
    }

    const city = tz.split('/').pop()?.replace(/_/g, ' ') || tz;
    const formattedLabel = `${tz.replace(/_/g, ' ')} (${offset})`;

    return {
      id: tz,
      label: formattedLabel,
      city,
      offset,
    };
  } catch {
    const city = tz.split('/').pop()?.replace(/_/g, ' ') || tz;
    return {
      id: tz,
      label: tz.replace(/_/g, ' '),
      city,
      offset: '',
    };
  }
}

const TIMEZONE_ALIASES: Record<string, string> = {
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Asia/Katmandu': 'Asia/Kathmandu',
  'Asia/Rangoon': 'Asia/Yangon',
  'Europe/Kiev': 'Europe/Kyiv',
  'US/Eastern': 'America/New_York',
  'US/Central': 'America/Chicago',
  'US/Mountain': 'America/Denver',
  'US/Pacific': 'America/Los_Angeles',
  'US/Hawaii': 'Pacific/Honolulu',
  'US/Alaska': 'America/Anchorage',
};

export function normalizeTimezone(tz: string): string {
  if (!tz) return '';
  if (TIMEZONE_ALIASES[tz]) {
    return TIMEZONE_ALIASES[tz];
  }
  return tz;
}

export function findTimezoneOption(
  tzId: string,
  optionsList: TimezoneOption[],
): TimezoneOption | undefined {
  if (!tzId) return undefined;

  let match = optionsList.find((tz) => tz.id === tzId);
  if (match) return match;

  const normalized = normalizeTimezone(tzId);
  match = optionsList.find((tz) => tz.id === normalized);
  if (match) return match;

  const lower = tzId.toLowerCase();
  match = optionsList.find((tz) => tz.id.toLowerCase() === lower);
  if (match) return match;

  const aliasKey = Object.keys(TIMEZONE_ALIASES).find((k) => TIMEZONE_ALIASES[k] === tzId);
  if (aliasKey) {
    match = optionsList.find((tz) => tz.id === aliasKey);
    if (match) return match;
  }

  return formatTimezoneOption(tzId);
}

export function getBrowserTimezone(): string {
  try {
    const resolved = Intl?.DateTimeFormat?.()?.resolvedOptions?.();
    const tz = resolved?.timeZone || '';
    return normalizeTimezone(tz);
  } catch {
    return '';
  }
}

export function getAllSupportedTimezones(): TimezoneOption[] {
  let timezones: string[] = [];
  try {
    if (typeof Intl !== 'undefined' && 'supportedValuesOf' in Intl) {
      timezones = (
        Intl as unknown as { supportedValuesOf: (key: string) => string[] }
      ).supportedValuesOf('timeZone');
    }
  } catch {
    timezones = [];
  }

  if (!timezones || timezones.length === 0) {
    timezones = [
      'UTC',
      'Asia/Kolkata',
      'Asia/Dubai',
      'Asia/Singapore',
      'Asia/Tokyo',
      'Europe/London',
      'Europe/Berlin',
      'Europe/Paris',
      'Europe/Moscow',
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Los_Angeles',
      'America/Sao_Paulo',
      'Australia/Sydney',
      'Pacific/Auckland',
    ];
  }

  return timezones.map((tz) => formatTimezoneOption(tz));
}

export function searchTimezones(query: string, allOptions: TimezoneOption[]): TimezoneOption[] {
  const q = query.trim().toLowerCase();
  if (!q) return allOptions;

  return allOptions.filter((opt) => {
    return (
      opt.id.toLowerCase().includes(q) ||
      opt.city.toLowerCase().includes(q) ||
      opt.offset.toLowerCase().includes(q) ||
      opt.label.toLowerCase().includes(q)
    );
  });
}

export function doSlotsOverlap(
  startA: string,
  endA: string,
  startB: string,
  endB: string,
): boolean {
  const aStartMin = timeToMinutes(startA);
  const aEndMin = timeToMinutes(endA);
  const bStartMin = timeToMinutes(startB);
  const bEndMin = timeToMinutes(endB);

  return aStartMin < bEndMin && bStartMin < aEndMin;
}

export function validateDaySlots(slots: TimeSlot[]): string | null {
  if (!slots || slots.length === 0) return null;

  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i];
    const sMin = timeToMinutes(slot.start);
    const eMin = timeToMinutes(slot.end);

    if (eMin <= sMin) {
      return 'End time must be after start time.';
    }

    const duration = eMin - sMin;
    if (duration < 60) {
      return 'Slot duration must be at least 1 hour.';
    }
    if (duration > 480) {
      return 'Slot duration cannot exceed 8 hours.';
    }
  }

  for (let i = 0; i < slots.length; i++) {
    for (let j = i + 1; j < slots.length; j++) {
      if (doSlotsOverlap(slots[i].start, slots[i].end, slots[j].start, slots[j].end)) {
        return 'Time slot cannot overlap with another slot on the same day.';
      }
    }
  }

  return null;
}

export function isSlotConflicting(slot: TimeSlot, allSlots: TimeSlot[]): boolean {
  const sMin = timeToMinutes(slot.start);
  const eMin = timeToMinutes(slot.end);

  if (eMin <= sMin || eMin - sMin < 60 || eMin - sMin > 480) {
    return true;
  }

  return allSlots.some(
    (other) => other.id !== slot.id && doSlotsOverlap(slot.start, slot.end, other.start, other.end),
  );
}

export function sortSlots(slots: TimeSlot[]): TimeSlot[] {
  return [...slots].sort((a, b) => timeToMinutes(a.start) - timeToMinutes(b.start));
}
